import axios from 'axios'
import { isEmbed } from '@embed/embedConfig'
import { requestAuthRefresh } from '@embed/embedAuth'
import { restoreUpstreamStatus } from './upstreamStatus'

/**
 * Clean API client for /api/v1/*.
 * No object type IDs, no field IDs, no session-id extraction.
 * Just forwards the Bearer token and returns domain objects.
 */

let getAccessTokenFn = null
let redirectingToLogin = false

export const setAccessTokenGetter = (getter) => {
  getAccessTokenFn = getter
  redirectingToLogin = false
}

const forceLogin = () => {
  if (redirectingToLogin) return
  redirectingToLogin = true
  // In embed mode we're inside an iframe — never redirect ourselves to
  // /login (would just show a login page inside the parent app). Ask the
  // parent to refresh the token and let the next call retry naturally.
  if (isEmbed()) {
    console.warn('Embed: token expired, requesting refresh from parent')
    requestAuthRefresh()
    return
  }
  console.warn('Session expired — redirecting to login')
  for (const key of Object.keys(localStorage)) {
    if (key.startsWith('@@auth0spajs@@')) localStorage.removeItem(key)
  }
  window.location.replace('/login')
}

const client = axios.create({
  baseURL: '/api/v1',
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
})

client.interceptors.request.use(async (config) => {
  if (getAccessTokenFn) {
    try {
      const token = await getAccessTokenFn()
      if (token) config.headers.Authorization = `Bearer ${token}`
    } catch {
      // getAccessTokenSilently() threw — token/session expired
      forceLogin()
      return Promise.reject(new axios.Cancel('Session expired'))
    }
  }
  return config
})

client.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (axios.isCancel(error) || redirectingToLogin) {
      return new Promise(() => {}) // Swallow — redirect in progress
    }

    // Spec 196: a 502/504 the /api/v1 Pages proxy had to carry on 503 (Cloudflare
    // replaces those two with its own page) gets its real status back here.
    restoreUpstreamStatus(error)

    const originalRequest = error.config

    if (error.response?.status === 401) {
      // First 401: one-time silent token refresh + retry. Recovers a merely
      // stale Auth0 token transparently.
      if (!originalRequest._retried && getAccessTokenFn) {
        originalRequest._retried = true
        try {
          const newToken = await getAccessTokenFn({ cacheMode: 'off' })
          if (newToken) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`
            return client(originalRequest)
          }
        } catch {
          // Refresh failed — token is truly expired or revoked; fall through.
        }
      }

      // No refresh available, or the retry STILL 401'd — the CB session is dead
      // even though Auth0 (localStorage) looks alive. Force a clean re-login to
      // mint a fresh CB session. This is what fixes the stale-session dead-end
      // that surfaced as "Failed to load reports" (CB "User not logged in").
      forceLogin()
      return new Promise(() => {}) // Hang the promise so components don't flash errors
    }

    return Promise.reject(error)
  }
)

// CC-50022: report RUNS get a longer leash than the global 30s. A report proc
// can legitimately take minutes under load (QA hit Fee Receipts timing out on a
// NARROWED window — the runtime tracks server load / plan quality, not result
// size), and the 30s abort turned that into "took too long" advice that
// narrowing can't fix. Interactive CRUD keeps the 30s default.
export const REPORT_RUN_TIMEOUT_MS = 120000

export const api = {
  get: async (path, params, config) => {
    const { data } = await client.get(path, { params, ...config })
    return data
  },
  post: async (path, body, config) => {
    const { data } = await client.post(path, body, config)
    return data
  },
  put: async (path, body) => {
    const { data } = await client.put(path, body)
    return data
  },
  delete: async (path) => {
    const { data } = await client.delete(path)
    return data
  },
  postFormData: async (path, formData) => {
    const { data } = await client.post(path, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return data
  },
}

export default client
